// §0.5.9 Provider-Facing Legend — ? button in global header opens indicator reference modal.

import { QuestionIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  CloseButton,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react'
import { ReactNode } from 'react'
import { GrayFlaskIcon, OrangeFlaskIcon, RedFlaskIcon } from '../icons/FlaskIcon'

interface LegendRow {
  visual: ReactNode
  name: string
  description: string
  location: string
}

const Dot = ({ color }: { color: string }) => (
  <Box w={3} h={3} borderRadius="full" bg={color} flexShrink={0} />
)

const LEGEND_ROWS: LegendRow[] = [
  {
    visual: <Dot color="red.500" />,
    name: 'Red dot',
    description: 'Unreviewed lab result — result returned but not yet acknowledged by provider.',
    location: 'Patient panel',
  },
  {
    visual: <Dot color="teal.400" />,
    name: 'Teal dot',
    description: 'Authentication status — patient/form authenticated by a provider.',
    location: 'Patient panel',
  },
  {
    visual: <GrayFlaskIcon size={20} />,
    name: 'Gray flask',
    description:
      'Overdue lab order — labs were ordered but no result has been received after 7 days.',
    location: 'Patient panel · Flow sheet',
  },
  {
    visual: <RedFlaskIcon size={20} />,
    name: 'Red flask',
    description: 'Unreviewed result — lab result returned with a flagged value; not yet reviewed.',
    location: 'Patient panel · Flow sheet',
  },
  {
    visual: <OrangeFlaskIcon size={20} />,
    name: 'Orange flask',
    description: 'Reviewed + flagged — flagged lab result reviewed and acknowledged by provider.',
    location: 'Patient panel · Flow sheet',
  },
  {
    visual: (
      <Text fontSize="sm" color="red.600" fontWeight={700} lineHeight={1}>
        !
      </Text>
    ),
    name: 'Red exclamation (!)',
    description:
      'Active provider flag — one or more text flags added by a provider or generated by the system.',
    location: 'Patient panel',
  },
  {
    visual: (
      <Box
        w="18px"
        h="18px"
        borderLeft="3px solid"
        borderColor="purple.400"
        bg="purple.50"
        _dark={{ bg: 'purple.900' }}
      />
    ),
    name: 'Purple left stripe',
    description: 'Delivered patient — this pregnancy has a recorded delivery date.',
    location: 'Patient panel row',
  },
  {
    visual: (
      <Flex gap={1}>
        {(['red', 'orange', 'yellow', 'green', 'blue', 'purple'] as const).map(c => (
          <Dot key={c} color={`${c}.400`} />
        ))}
      </Flex>
    ),
    name: 'Color dot tags',
    description:
      'Provider-assigned custom tag — each provider can assign one color per patient. Labels are configurable in Provider Settings.',
    location: 'Patient panel',
  },
  {
    visual: (
      <Badge colorScheme="red" fontSize="xs" fontFamily="Hero-New">
        In Labor
      </Badge>
    ),
    name: 'In Labor chip',
    description:
      'Patient is actively in labor. Set when admission encounter saved; cleared when delivery recorded.',
    location: 'Patient panel',
  },
  {
    visual: (
      <Badge colorScheme="gray" fontSize="xs" fontFamily="Hero-New">
        Pending
      </Badge>
    ),
    name: 'Pending chip',
    description: 'Encounter seen — awaiting follow-up action. Provider marks resolved to clear.',
    location: 'Patient panel (GYN tab)',
  },
  {
    visual: (
      <Text color="red.500" fontSize="xs" fontWeight={600} fontFamily="Open Sans">
        140/90
      </Text>
    ),
    name: 'Red clinical value',
    description:
      'Value is outside the normal range for that measurement. Historical — stays red; does not clear.',
    location: 'Flow sheet · Visit note',
  },
  {
    visual: (
      <Text fontSize="xs" color="gray.500" fontFamily="Hero-New">
        ▲
      </Text>
    ),
    name: 'Weight bypass ▲',
    description:
      'Weight entry was bypassed by the provider this visit. Historical — does not clear.',
    location: 'Flow sheet weight cell',
  },
]

const LegendTable = () => (
  <Table size="sm" variant="simple">
    <Thead h="36px">
      <Tr>
        <Th textAlign="center" fontFamily="Hero-New" w="60px">
          Icon
        </Th>
        <Th fontFamily="Hero-New">Indicator</Th>
        <Th fontFamily="Hero-New">Meaning</Th>
        <Th fontFamily="Hero-New">Where</Th>
      </Tr>
    </Thead>
    <Tbody>
      {LEGEND_ROWS.map((row, idx) => (
        <Tr borderBottomWidth={idx === LEGEND_ROWS.length - 1 ? 0 : 1} key={row.name}>
          <Td>
            <Flex align="center" justify="center" minH="20px">
              {row.visual}
            </Flex>
          </Td>
          <Td>
            <Text fontFamily="Hero-New" fontWeight={600} fontSize="xs">
              {row.name}
            </Text>
          </Td>
          <Td>
            <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.200' }} lineHeight={1.4}>
              {row.description}
            </Text>
          </Td>
          <Td>
            <Text
              fontSize="xs"
              color="gray.500"
              _dark={{ color: 'gray.200' }}
              fontFamily="Hero-New"
              whiteSpace="nowrap">
              {row.location}
            </Text>
          </Td>
        </Tr>
      ))}
    </Tbody>
  </Table>
)

export const IndicatorLegendButton = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  return (
    <>
      <Tooltip label="Indicator legend" hasArrow placement="bottom">
        <IconButton
          aria-label="Indicator legend"
          icon={<QuestionIcon />}
          size="sm"
          variant="ghost"
          colorScheme="gray"
          onClick={onOpen}
        />
      </Tooltip>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent _dark={{ bg: 'gray.800' }}>
          <ModalHeader
            borderBottomWidth={1}
            borderColor="gray.200"
            _dark={{ borderColor: 'gray.700' }}
            fontFamily="Hero-New"
            py={2}
            pr={2}
            pl={4}
            bg="gray.50"
            borderTopRadius="md"
            fontSize="lg">
            <Flex align="center" w="100%">
              <Text fontWeight={500} flex={1} minW={0}>
                Indicator Legend
              </Text>
              <CloseButton onClick={onClose} />
            </Flex>
          </ModalHeader>
          <ModalBody py={0} px={0}>
            <LegendTable />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}
